import { GroupBooking, VenueRequestFile, VenueRequestStatus } from '@go-shared/types/groupBooking';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueConfiguration, VenueTemplate } from '@go-shared/types/venueTemplate';
import React, { useState } from 'react';

import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTypeahead,
} from '../BubbleUI';
import BubbleFileM from '../BubbleUI/BubbleFileM';
import { FormGenericContainer } from '../FormGenericContainer';
import { BookingForm, GroupForm } from './Workflows';
import FormStatusFooter from './FormStatusFooter';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import { hasVenueHoursConflict } from '@go-shared/lib/plan';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { timeToMinutes } from '@go-shared/lib/time';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canBookRequestsBypassingTimeRestrictions } from '@go-shared/lib/permissions';
import TinyEditor from '../TinyEditor';
import { GroupResource } from '@go-shared/types/zod/groupResource';

interface EnterBookingDetailsPageProps {
  bookingForm: BookingForm;
  changeBookingForm: <T extends keyof BookingForm>(key: T, val: BookingForm[T]) => void;
  clearBookingForm?: () => void;
  venueConfigOpts: VenueConfiguration[];
  groupResources: GroupResource[];
  title?: string;
  pageIndex?: number;
  isNewGroup: boolean;
  groupForm: GroupForm;
  previousStatus?: VenueRequestStatus;
  selectedGroup?: GroupBooking;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  selectedVenue: VenueTemplate;
  showDayContext?: boolean;
  hideDayContextButton?: boolean;
  hideDraft?: boolean;
  refreshing?: boolean;
  submitTitle?: string;
  onGoBack?: () => void;
  onGoForward?: () => void;
  onClose?: () => void;
  onToggleShowDayContext?: () => void;
  onSubmitBookingForm: (status?: VenueRequestStatus) => Promise<void>;
  onUpdateFile: (evt: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterBookingPage(props: EnterBookingDetailsPageProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [isBusy, setIsBusy] = useState(false);

  const availableResources = props.groupResources.map(r => ({
    key: r._id,
    refKey: `${r.name} - ($${r.price})`,
    item: { ...r, amount: 0 },
  }));

  async function handleOnSubmitGroupBooking(status?: VenueRequestStatus) {
    const statusToUse =
      props.previousStatus === 'approved' ? 'modified' : status || props.previousStatus;

    setIsBusy(true);

    try {
      if (props.onSubmitBookingForm) await props.onSubmitBookingForm(statusToUse);
      if (props.onGoForward) props.onGoForward();
    } finally {
      setIsBusy(false);
    }
  }

  const formValid =
    props.bookingForm.name.length > 0 && props.bookingForm.startTime && props.bookingForm.endTime;

  const meetsTimeBoundary =
    !hasVenueHoursConflict(
      props.selectedVenue.bookingHours,
      props.bookingForm.startTime,
      props.bookingForm.endTime,
      timeToMinutes(activeProperty.dayStart || DEFAULT_DAY_START)
    ) || canBookRequestsBypassingTimeRestrictions(user);

  const canSubmit = !isBusy && formValid && meetsTimeBoundary;

  return (
    <div className="tflf-3">
      <FormGenericContainer
        footer={() => (
          <>
            <FormStatusFooter {...props} className="text-slate-dark mr-auto" />
            {props.onGoBack && (
              <BubbleButton variant="navy" inverse onClick={props.onGoBack} disabled={isBusy}>
                Go Back
              </BubbleButton>
            )}
            {!props.hideDraft && (
              <BubbleButton
                variant="banana"
                className="mr-3"
                disabled={!canSubmit}
                onClick={() => handleOnSubmitGroupBooking('draft')}
              >
                Save as Draft
              </BubbleButton>
            )}
            <BubbleButton
              variant="navy"
              disabled={!canSubmit}
              onClick={() => handleOnSubmitGroupBooking()}
            >
              {props.submitTitle || 'Finish: Submit Group Booking Request'}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <div className="flex items-center">
              <h1
                className="text-lg text-slate-dark font-semibold"
                dangerouslySetInnerHTML={{ __html: props.title || 'Create Group Booking' }}
              />
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Group Event Details
            </h2>
          </div>
          <div className="ml-auto" />
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div>
          {props.clearBookingForm && (
            <div className="-my-5 text-right relative">
              <BubbleButton variant="smoke" inverse onClick={props.clearBookingForm} icon="eraser">
                Clear Form
              </BubbleButton>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 relative">
            <BubbleFormGroup label="Group Event Details" className="col-span-3" asterisk>
              <BubbleInput
                block
                type="text"
                value={props.bookingForm.name}
                onValue={val => props.changeBookingForm('name', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Start Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.startTime}
                onValue={val => props.changeBookingForm('startTime', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="End Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.endTime}
                onValue={val => props.changeBookingForm('endTime', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Guests (min. 0)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.numGuests}
                onValue={val => props.changeBookingForm('numGuests', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Set Up Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.setUpTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    setUpTime: val,
                  })
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Breakdown Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.breakdownTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    breakdownTime: val,
                  })
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Food & Beverages" className="col-span-3">
              <BubbleTypeahead
                block
                options={availableResources.filter(
                  r =>
                    !props.bookingForm.resources?.some(res => res.resourceId === r.key) &&
                    r.item.resourceType === 'F&B'
                )}
                placeholder="Enter food or beverage name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                suggestionsText="No similar food or beverage names found"
                suggestionClassName="p-1-i pl-4-i"
                onCreate={val => {
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val,
                      amount: 1,
                      id: '',
                      archived: false,
                      resourceType: 'F&B',
                      resourceId: '',
                      price: 0,
                    },
                  ]);
                }}
                onAdd={val => {
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val.item.name,
                      amount: 1,
                      id: '',
                      archived: val.item.archived,
                      resourceType: val.item.resourceType,
                      resourceId: val.item._id,
                      price: val.item.price,
                    },
                  ]);
                }}
              />

              {props.bookingForm.resources
                ?.filter(r => r.resourceType === 'F&B')
                .map(resource => (
                  <div key={resource.resourceId} className="flex items-center mt-2 space-x-3">
                    <span className="flex-grow">
                      {resource.name} - (${resource.price})
                    </span>

                    {!resource.resourceId && (
                      <BubbleFormGroup label="Price">
                        <BubbleInput
                          block
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Enter name"
                          value={resource.price}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r === resource ? { ...r, price: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                          className="w-40"
                        />
                      </BubbleFormGroup>
                    )}

                    <div className="flex items-end">
                      <BubbleFormGroup label="Amount (min. 0)">
                        <BubbleInput
                          block
                          size="medium"
                          type="number"
                          min="0"
                          value={resource.amount}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r.resourceId === resource.resourceId ? { ...r, amount: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                        />
                      </BubbleFormGroup>

                      <BubbleButton
                        onClick={() => {
                          props.changeBookingForm(
                            'resources',
                            props.bookingForm.resources.filter(
                              r => r.resourceId !== resource.resourceId
                            )
                          );
                        }}
                        className="p-2-i"
                        variant="crimson"
                        inverse
                        icon="trash-alt"
                      />
                    </div>
                  </div>
                ))}
            </BubbleFormGroup>

            <BubbleFormGroup label="Entertainment" className="col-span-3">
              <BubbleTypeahead
                block
                options={availableResources.filter(
                  r =>
                    !props.bookingForm.resources?.some(res => res.resourceId === r.key) &&
                    r.item.resourceType === 'ENT'
                )}
                placeholder="Enter entertainment name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                suggestionsText="No similar entertainment names found"
                suggestionClassName="p-1-i pl-4-i"
                onCreate={val => {
                  console.log('[onCreate]', val);
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val,
                      amount: 1,
                      id: '',
                      archived: false,
                      resourceType: 'ENT',
                      resourceId: '',
                      price: 0,
                    },
                  ]);
                }}
                onAdd={val => {
                  console.log('[onAdd]', val);
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val.item.name,
                      amount: 1,
                      id: '',
                      archived: val.item.archived,
                      resourceType: val.item.resourceType,
                      resourceId: val.item._id,
                      price: val.item.price,
                    },
                  ]);
                }}
              />

              {props.bookingForm.resources
                ?.filter(r => r.resourceType === 'ENT')
                .map(resource => (
                  <div key={resource.resourceId} className="flex items-center mt-2 space-x-3">
                    <span className="flex-grow">
                      {resource.name} - (${resource.price})
                    </span>

                    {!resource.resourceId && (
                      <BubbleFormGroup label="Price">
                        <BubbleInput
                          block
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Enter name"
                          value={resource.price}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r === resource ? { ...r, price: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                          className="w-40"
                        />
                      </BubbleFormGroup>
                    )}

                    <div className="flex items-end">
                      <BubbleFormGroup label="Amount (min. 0)">
                        <BubbleInput
                          block
                          size="medium"
                          type="number"
                          min="0"
                          value={resource.amount}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r.resourceId === resource.resourceId ? { ...r, amount: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                        />
                      </BubbleFormGroup>

                      <BubbleButton
                        onClick={() => {
                          props.changeBookingForm(
                            'resources',
                            props.bookingForm.resources.filter(
                              r => r.resourceId !== resource.resourceId
                            )
                          );
                        }}
                        className="p-2-i"
                        variant="crimson"
                        inverse
                        icon="trash-alt"
                      />
                    </div>
                  </div>
                ))}
            </BubbleFormGroup>

            <BubbleFormGroup label="Housekeeping Requests" className="col-span-3">
              <BubbleTypeahead
                block
                options={availableResources.filter(
                  r =>
                    !props.bookingForm.resources?.some(res => res.resourceId === r.key) &&
                    r.item.resourceType === 'HSK'
                )}
                placeholder="Enter housekeeping name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                suggestionsText="No similar housekeeping names found"
                suggestionClassName="p-1-i pl-4-i"
                onCreate={val => {
                  console.log('[onCreate]', val);
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val,
                      amount: 1,
                      id: '',
                      archived: false,
                      resourceType: 'HSK',
                      resourceId: '',
                      price: 0,
                    },
                  ]);
                }}
                onAdd={val => {
                  console.log('[onAdd]', val);
                  props.changeBookingForm('resources', [
                    ...(props.bookingForm.resources || []),
                    {
                      name: val.item.name,
                      amount: 1,
                      id: '',
                      archived: val.item.archived,
                      resourceType: val.item.resourceType,
                      resourceId: val.item._id,
                      price: val.item.price,
                    },
                  ]);
                }}
              />

              {props.bookingForm.resources
                ?.filter(r => r.resourceType === 'HSK')
                .map(resource => (
                  <div key={resource.resourceId} className="flex items-center mt-2 space-x-3">
                    <span className="flex-grow">
                      {resource.name} - (${resource.price})
                    </span>

                    {!resource.resourceId && (
                      <BubbleFormGroup label="Price">
                        <BubbleInput
                          block
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Enter name"
                          value={resource.price}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r === resource ? { ...r, price: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                          className="w-40"
                        />
                      </BubbleFormGroup>
                    )}

                    <div className="flex items-end">
                      <BubbleFormGroup label="Amount (min. 0)">
                        <BubbleInput
                          block
                          size="medium"
                          type="number"
                          min="0"
                          value={resource.amount}
                          onValue={val => {
                            const newResources = props.bookingForm.resources.map(r =>
                              r.resourceId === resource.resourceId ? { ...r, amount: val } : r
                            );
                            props.changeBookingForm('resources', newResources);
                          }}
                        />
                      </BubbleFormGroup>

                      <BubbleButton
                        onClick={() => {
                          props.changeBookingForm(
                            'resources',
                            props.bookingForm.resources.filter(
                              r => r.resourceId !== resource.resourceId
                            )
                          );
                        }}
                        className="p-2-i"
                        variant="crimson"
                        inverse
                        icon="trash-alt"
                      />
                    </div>
                  </div>
                ))}
            </BubbleFormGroup>

            <BubbleFormGroup label="Venue Configuration" className="col-span-3">
              <BubbleSelect
                block
                allowUndefined
                labelExtractor={c => `${c.name} (occ. ${c.occupancy})`}
                options={props.venueConfigOpts}
                value={props.bookingForm.venueConfiguration}
                onValue={val => props.changeBookingForm('venueConfiguration', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Group Event Notes" className="col-span-3">
              <TinyEditor
                value={props.bookingForm.notes}
                onValue={val => props.changeBookingForm('notes', val)}
                mode="text"
              />
            </BubbleFormGroup>
            <div className="col-span-3">
              <BubbleFileM
                label="Group Event Files"
                files={props.bookingForm.files}
                onUpdateFile={props.onUpdateFile}
                onUploadFile={props.onUploadFile}
                onDeleteFile={props.onDeleteFile}
              />
            </div>
            <div className="col-span-3">
              {!meetsTimeBoundary && (
                <BubbleAlert variant="banana">
                  To submit this booking, its time must be within open bookings hours as specified
                  on the venue.
                </BubbleAlert>
              )}
            </div>
          </div>
        </div>
      </FormGenericContainer>
    </div>
  );
}
